import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { TaskState } from '~/services/cache/types'
import type { AppContextType } from '~/app-context'
import type { BankTransaction } from '~/storage/types'
import { chunkArray } from '~/utils/array-utils'
import { normalizeDateFromFreeText } from '~common/utils/normalizeDateFromFreeText/normalizeDateFromFreeText'
import * as XLSX from 'xlsx'

const INTERNAL_FIELDS = [
    'vendorName',
    'counterpartyIban',
    'date',
    'amount',
    'referenceText',
    'note',
] as const
type InternalField = (typeof INTERNAL_FIELDS)[number]

export const DATE_FORMATS = [
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'DD.MM.YYYY',
] as const

export const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'] as const

export const DECIMAL_SEPARATORS = ['dot', 'comma', 'cents'] as const

export interface UploadTransactionsFormLogicDependencies {
    onClose: () => void
}

export interface UploadTransactionsFormLogicState {
    accountName: string
    iban: string
    dateFormat: (typeof DATE_FORMATS)[number]
    currency: (typeof CURRENCIES)[number]
    decimalSep: (typeof DECIMAL_SEPARATORS)[number]
    fieldMapping: Record<InternalField, string | null>
    parsedCSV: {
        headers: string[]
        rows: string[][]
    } | null
    file: File | null
    loadState: TaskState
    loadingStage: 'importing' | 'matching' | null
    canSubmit: boolean
    errorMessage: string | null
}

export class UploadTransactionsFormLogic extends Logic<
    UploadTransactionsFormLogicDependencies & AppContextType,
    UploadTransactionsFormLogicState
> {
    getInitialState = (): UploadTransactionsFormLogicState => ({
        accountName: '',
        iban: '',
        dateFormat: 'DD/MM/YYYY',
        currency: 'EUR',
        decimalSep: 'dot',
        fieldMapping: {
            vendorName: null,
            counterpartyIban: null,
            date: null,
            amount: null,
            referenceText: null,
            note: null,
        },
        parsedCSV: null,
        file: null,
        loadState: 'pristine',
        loadingStage: null,
        canSubmit: false,
        errorMessage: null,
    })

    async initialize() {
        await executeTask(this, 'loadState', async () => {})
        const saved = localStorage.getItem('upload-transactions-form-settings')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                const canSubmit = this.isSubmitEnabled()
                this.setState({
                    accountName: parsed.accountName ?? this.state.accountName,
                    iban: parsed.iban ?? this.state.iban,
                    dateFormat: parsed.dateFormat ?? this.state.dateFormat,
                    currency: parsed.currency ?? this.state.currency,
                    decimalSep: parsed.decimalSep ?? this.state.decimalSep,
                    fieldMapping: {
                        ...this.state.fieldMapping,
                        ...parsed.fieldMapping,
                    },
                    canSubmit,
                })
            } catch (e) {
                // ignore invalid saved data
            }
        }
    }

    updateAccountName = (value: string) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({ accountName: value })
        this.saveSettings()
    }

    updateIban = (value: string) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({ iban: value, canSubmit })
        this.saveSettings()
    }

    updateDateFormat = (value: (typeof DATE_FORMATS)[number]) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({ dateFormat: value, canSubmit })
        this.saveSettings()
    }

    updateCurrency = (value: (typeof CURRENCIES)[number]) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({ currency: value, canSubmit })
        this.saveSettings()
    }

    updateDecimalSep = (value: (typeof DECIMAL_SEPARATORS)[number]) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({ decimalSep: value, canSubmit })
        this.saveSettings()
    }

    updateFieldMapping = (field: InternalField, value: string | null) => {
        const canSubmit = this.isSubmitEnabled()
        this.setState({
            fieldMapping: {
                ...this.state.fieldMapping,
                [field]: value,
            },
            canSubmit,
            errorMessage: null,
        })
        this.saveSettings()
    }

    handleFileUpload = async (file: File) => {
        await executeTask(this, 'loadState', async () => {
            const { headers, rows: allRows } = await this.parseFile(file)

            if (headers.length === 0) return

            const rows = allRows.slice(0, 20)

            const availableHeaders = new Set(headers)
            const prevMapping = this.state.fieldMapping
            const nextMapping: Record<InternalField, string | null> = {
                vendorName:
                    prevMapping.vendorName &&
                    availableHeaders.has(prevMapping.vendorName)
                        ? prevMapping.vendorName
                        : null,
                counterpartyIban:
                    prevMapping.counterpartyIban &&
                    availableHeaders.has(prevMapping.counterpartyIban)
                        ? prevMapping.counterpartyIban
                        : null,
                date:
                    prevMapping.date && availableHeaders.has(prevMapping.date)
                        ? prevMapping.date
                        : null,
                amount:
                    prevMapping.amount &&
                    availableHeaders.has(prevMapping.amount)
                        ? prevMapping.amount
                        : null,
                referenceText:
                    prevMapping.referenceText &&
                    availableHeaders.has(prevMapping.referenceText)
                        ? prevMapping.referenceText
                        : null,
                note:
                    prevMapping.note && availableHeaders.has(prevMapping.note)
                        ? prevMapping.note
                        : null,
            }

            this.setState({
                file,
                parsedCSV: { headers, rows },
                fieldMapping: nextMapping,
                errorMessage: null,
            })
            const canSubmit = this.isSubmitEnabled()
            this.setState({ canSubmit })
            this.saveSettings()
        })
    }

    async submitForm() {
        if (!this.state.file || !this.deps.storageModules.bankTransaction) {
            return
        }

        this.setState({ errorMessage: null })

        await executeTask(this, 'loadState', async () => {
            try {
                // Stage 1: Importing Transactions
                this.setState({ loadingStage: 'importing' })

                const { headers, rows: allRows } = await this.parseFile(
                    this.state.file!,
                )
                if (headers.length === 0) return

                const {
                    accountName,
                    iban: accountIban,
                    dateFormat,
                    currency,
                    decimalSep,
                    fieldMapping,
                } = this.state

                // Ensure bank account exists
                const existingAccounts =
                    await this.deps.services.bankTransaction.getAllBankAccounts()
                const accountExists = existingAccounts.some(
                    (acc) => acc.iban === accountIban,
                )
                if (!accountExists && accountName && accountIban) {
                    await this.deps.services.bankTransaction.saveBankAccount({
                        name: accountName,
                        iban: accountIban,
                        currency: this.state.currency,
                        dateFormat: this.state.dateFormat,
                        amountFormat: this.state.decimalSep,
                    })
                }

                const headerToIndex = new Map(headers.map((h, i) => [h, i]))

                const transactions: Omit<BankTransaction, 'id'>[] = []

                for (const row of allRows) {
                    // Skip entirely empty rows
                    if (row.every((cell) => !cell || cell.trim() === '')) {
                        continue
                    }

                    const transactionData: Omit<
                        BankTransaction,
                        'id' | 'createdAt' | 'updatedAt'
                    > = {
                        accountIban,
                        vendorName: this.getFieldValue(
                            row,
                            fieldMapping.vendorName,
                            headerToIndex,
                        ),
                        counterpartyIban: this.getFieldValue(
                            row,
                            fieldMapping.counterpartyIban,
                            headerToIndex,
                        ),
                        date: undefined as unknown as number,
                        amount: undefined as unknown as number,
                        currency,
                        referenceText: this.getFieldValue(
                            row,
                            fieldMapping.referenceText,
                            headerToIndex,
                        ),
                        note: this.getFieldValue(
                            row,
                            fieldMapping.note,
                            headerToIndex,
                        ),
                        status: 'active',
                    }

                    // Parse date
                    const dateHeader = fieldMapping.date
                    if (dateHeader) {
                        const dateStr = this.getFieldValue(
                            row,
                            dateHeader,
                            headerToIndex,
                        )
                        try {
                            transactionData.date = this.parseDate(dateStr)
                            if (
                                transactionData.date === 0 ||
                                transactionData.date === null
                            ) {
                                throw new Error(`Invalid date: ${dateStr}`)
                            }
                        } catch (e) {
                            throw new Error(`Invalid date: ${dateStr}`)
                        }
                    }

                    // Parse amount
                    const amountHeader = fieldMapping.amount
                    if (amountHeader) {
                        let amountStr: string | undefined = this.getFieldValue(
                            row,
                            amountHeader,
                            headerToIndex,
                        )
                        if (decimalSep === 'comma') {
                            amountStr = amountStr.replace(',', '.')
                        } else if (decimalSep === 'cents') {
                            const num = parseInt(
                                amountStr.replace(/[^\d-]/g, ''),
                                10,
                            )
                            amountStr = isNaN(num)
                                ? undefined
                                : (num / 100).toFixed(2)
                        }
                        if (amountStr == null || amountStr === '') {
                            throw new Error(
                                'you need to put your amounts in a single column with positive and negative values',
                            )
                        }
                        transactionData.amount = parseFloat(amountStr)
                    }

                    if (
                        transactionData.amount === undefined ||
                        transactionData.date === undefined
                    ) {
                        throw new Error('Invalid transaction data')
                    }

                    const now = Date.now()

                    const fullTransaction: Omit<BankTransaction, 'id'> = {
                        ...transactionData,
                        createdAt: now,
                        updatedAt: now,
                    }

                    transactions.push(fullTransaction)
                }

                if (transactions.length === 0) return

                const bankTransactionService =
                    this.deps.services.bankTransaction
                let transactionIds: string[] = []
                try {
                    transactionIds = (
                        await bankTransactionService.saveTransactions(
                            transactions,
                        )
                    ).ids
                } catch (e) {
                    throw new Error('Error saving transactions')
                }

                // Stage 2: Matching Invoices
                this.setState({ loadingStage: 'matching' })

                // Process matching in batches of 50 and wait for completion
                const matchBatches = chunkArray(transactionIds, 50)

                for (const batch of matchBatches) {
                    await Promise.allSettled(
                        batch.map((id) =>
                            this.deps.services.transactionInvoiceMatching.findMatchesForTransaction(
                                id,
                            ),
                        ),
                    )
                }
            } catch (e: unknown) {
                const error = e instanceof Error ? e : new Error(String(e))
                this.setState({ errorMessage: error.message })
                throw error
            }
        })

        this.setState({ loadingStage: null })
        this.deps.onClose()
    }

    isSubmitEnabled(): boolean {
        const { accountName, iban, parsedCSV, fieldMapping } = this.state

        const requiredMappingsPresent = Boolean(
            fieldMapping.date && fieldMapping.amount && fieldMapping.vendorName,
        )

        return Boolean(
            accountName && iban && parsedCSV && requiredMappingsPresent,
        )
    }

    getMissingRequiredFields(): string[] {
        const { accountName, iban, parsedCSV, fieldMapping } = this.state
        const missing: string[] = []

        if (!accountName.trim()) {
            missing.push('Account Name')
        }
        if (!iban.trim()) {
            missing.push('IBAN')
        }
        if (!parsedCSV) {
            missing.push('CSV or Excel file')
        }
        if (!fieldMapping.date) {
            missing.push('Date column')
        }
        if (!fieldMapping.amount) {
            missing.push('Amount column')
        }
        if (!fieldMapping.vendorName) {
            missing.push('Vendor Name column')
        }

        return missing
    }

    private async parseFile(
        file: File,
    ): Promise<{ headers: string[]; rows: string[][] }> {
        const content = await file.arrayBuffer()
        const workbook = XLSX.read(content, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            raw: false,
        }) as string[][]

        if (jsonData.length === 0) return { headers: [], rows: [] }

        const headers = jsonData[0].map((h) => String(h).trim())
        const rows = jsonData
            .slice(1)
            .filter((row) =>
                row.some(
                    (cell) =>
                        cell !== undefined &&
                        cell !== '' &&
                        String(cell).trim() !== '',
                ),
            )
            .map((row) => row.map((cell) => String(cell).trim()))

        return { headers, rows }
    }

    private saveSettings() {
        const {
            accountName,
            iban,
            dateFormat,
            currency,
            decimalSep,
            fieldMapping,
        } = this.state
        localStorage.setItem(
            'upload-transactions-form-settings',
            JSON.stringify({
                accountName,
                iban,
                dateFormat,
                currency,
                decimalSep,
                fieldMapping,
            }),
        )
    }

    private getFieldValue(
        row: string[],
        header: string | null,
        headerToIndex: Map<string, number>,
    ): string {
        if (!header) return ''
        const idx = headerToIndex.get(header)
        return idx !== undefined ? row[idx] || '' : ''
    }

    private parseDate(dateStr: string): number {
        if (!dateStr.trim()) return 0
        dateStr = dateStr.trim()

        const timestamp = normalizeDateFromFreeText(dateStr, {
            currency: this.state.currency,
            formatHint: this.state.dateFormat,
        })
        if (timestamp) {
            return timestamp
        }
        return 0
    }
}
